import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { OrgAppContext } from '../../OrgAppContext'
import { getProviderRole } from '../../utils/provider'
import {
  AlertEvent,
  AccessOutsideWorkingHoursData,
  AccessOutsideWorkingDaysData,
  ExcessiveAccessData,
  AccessAfterDischargedData,
  AccessAfterLongPeriodData,
  InactiveProviderAccessData,
} from '../../utils/interfaces'

interface AlertReasonProps {
  event: AlertEvent
}

function AlertReason({ event }: AlertReasonProps) {
  const { t } = useTranslation()
  const { customerId } = useContext(OrgAppContext)

  const providerRole = getProviderRole(customerId)

  const { type, data } = event

  const getReason = () => {
    switch (type) {
      case 'AccessAfterDischarged': {
        const typedData = data as AccessAfterDischargedData
        const months = typedData.duration.toFixed(0)
        return t('alertReasons.AccessAfterDischarged', { months, providerRole })
      }
      case 'AccessAfterLongPeriod': {
        const typedData = data as AccessAfterLongPeriodData
        const months = typedData.duration.toFixed(0)
        return t('alertReasons.AccessAfterLongPeriod', { months, providerRole })
      }
      case 'AccessOutsideWorkingHours': {
        const typedData = data as AccessOutsideWorkingHoursData
        const hour = typedData.accessHour
        if (typedData.count === 0) {
          return t('alertReasons.AccessOutsideWorkingHours_never', { hour, providerRole })
        }
        return t('alertReasons.AccessOutsideWorkingHours_seldom', { hour, providerRole })
      }
      case 'AccessOutsideWorkingDays': {
        const typedData = data as AccessOutsideWorkingDaysData
        const weekdays = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ]
        const dayName = weekdays[typedData.accessWeekday] || `Day ${typedData.accessWeekday}`
        if (typedData.count === 0) {
          return t('alertReasons.AccessOutsideWorkingDays_never', { dayName, providerRole })
        }
        return t('alertReasons.AccessOutsideWorkingDays_seldom', { dayName, providerRole })
      }
      case 'ExcessiveAccess': {
        const typedData = data as ExcessiveAccessData
        const count = typedData.accessCount
        const average = typedData.averageDailyAccess.toFixed(0)
        return t('alertReasons.ExcessiveAccess', { count, average, providerRole })
      }
      case 'InactiveProviderAccess': {
        const typedData = data as InactiveProviderAccessData
        const months = typedData.duration.toFixed(0)
        return t('alertReasons.InactiveProviderAccess', { months, providerRole })
      }
      case 'ProviderFirstAccess':
        return t('alertReasons.ProviderFirstAccess', { providerRole })
      case 'AccessVIP':
        return t('alertReasons.AccessVIP', { providerRole })
      case 'AccessSelf':
        return t('alertReasons.AccessSelf', { providerRole })
      case 'AccessInternal':
        return t('alertReasons.AccessInternal', { providerRole })
      case 'AccessFamily':
        return t('alertReasons.AccessFamily', { providerRole })
      case 'AccessRelatives':
        return t('alertReasons.AccessRelatives', { providerRole })
      case 'AccessNeighbors':
        return t('alertReasons.AccessNeighbors', { providerRole })

      default:
        return ''
    }
  }

  return <>{getReason()}</>
}

export default AlertReason
